import { REPORTING_CURRENCIES, type ReportingCurrency as Code } from '../../api/reportingCurrencies'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Select } from '../../platform/primitives/Select'
import { useReportingCurrencyState, useSetReportingCurrency } from './useReportingCurrency'

const names = typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames(undefined, { type: 'currency' }) : null

function optionLabel(code: string): string {
  let name: string | undefined
  try {
    name = names?.of(code)
  } catch {
    name = undefined
  }
  return name && name !== code ? `${code} — ${name}` : code
}

function asCode(value: string): Code | null {
  return REPORTING_CURRENCIES.find((c) => c === value) ?? null
}

/** Settings › Preferences › Currency: the currency reports and totals convert to. */
export function ReportingCurrency() {
  const state = useReportingCurrencyState()
  const set = useSetReportingCurrency()
  const { show } = useToast()

  return (
    <>
      <h2 className="preferences__title">Currency</h2>
      <Async state={state} surface="preferences" skeleton={<Skeleton />} empty={{ title: 'No currency yet' }}>
        {({ chosen, effective }) => (
          <div className="preferences__row">
            <div className="preferences__text">
              <span className="preferences__name">Reporting currency</span>
              <p className="preferences__help" id="currency-help">
                Reports and totals are converted to this currency at daily rates. Each transaction keeps its own
                currency.
              </p>
            </div>
            <Select
              label="Reporting currency"
              aria-describedby="currency-help"
              value={chosen ?? ''}
              onChange={(e) =>
                set.mutate(asCode(e.target.value), {
                  onError: () =>
                    show({ kind: 'error', message: 'We couldn’t save that. Your reporting currency is unchanged.' }),
                })
              }
            >
              <option value="">{effective ? `Automatic (currently ${effective})` : 'Automatic'}</option>
              {REPORTING_CURRENCIES.map((code) => (
                <option key={code} value={code}>
                  {optionLabel(code)}
                </option>
              ))}
            </Select>
          </div>
        )}
      </Async>
    </>
  )
}
